import {
    buildTimeboxedDayGroups,
    type ScheduleDayGroup,
    type ShiftScheduleSegment,
    type ShiftTimelineItem,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_model';
import { type Shift } from '@libraries/typescript/api_client';

const EXCLUDED_SCHEDULE_STATUSES = new Set<Shift['status']>([
    'cancelled',
    'missing',
    'not_scheduled',
]);
const OPEN_SCHEDULE_STATUSES = new Set<Shift['status']>([
    'needs_coverage',
    'open',
]);

/**
 * Maps a client's shifts onto the shared schedule day-group model used by
 * the availability month grid. `details` carries the covering caregiver's
 * first name; unfilled shifts render as "Open".
 */
export function buildClientScheduleDayGroups({
    shifts,
    timezone,
}: {
    readonly shifts: ReadonlyArray<Shift>;
    readonly timezone: string;
}): Array<ScheduleDayGroup<ShiftScheduleSegment>> {
    const items: Array<ShiftTimelineItem> = shifts
        .filter((shift) => !EXCLUDED_SCHEDULE_STATUSES.has(shift.status))
        .map((shift) => {
            const open = OPEN_SCHEDULE_STATUSES.has(shift.status);
            const caregiverName =
                shift.caregiver?.first_name ?? shift.caregiver?.name;
            return {
                kind: 'shift',
                type: 'shift',
                id: shift.id,
                startTime: shift.shift_start_time,
                endTime: shift.shift_end_time,
                details: open ? 'Open' : (caregiverName ?? 'Assigned'),
                open,
            };
        });
    // Shift-only input means every produced segment is a shift segment.
    return buildTimeboxedDayGroups(items, timezone) as Array<
        ScheduleDayGroup<ShiftScheduleSegment>
    >;
}

export function countClientScheduleFill(
    groups: ReadonlyArray<ScheduleDayGroup<ShiftScheduleSegment>>,
): { readonly filled: number; readonly open: number } {
    let filled = 0;
    let open = 0;
    const countedShiftIds = new Set<string>();
    for (const group of groups) {
        for (const segment of group.items) {
            // A multi-day shift produces one segment per day; count the
            // underlying shift once.
            if (countedShiftIds.has(segment.parentId)) {
                continue;
            }
            countedShiftIds.add(segment.parentId);
            if (segment.open) {
                open += 1;
            } else {
                filled += 1;
            }
        }
    }
    return { filled, open };
}
